/**
 * The scan card's rim, turning.
 *
 * The canvas specifies the rim as a still gradient — amber out of the top-left, purple into
 * the bottom-right. This keeps that exact ramp and rotates it, so the two colours travel
 * around the frame instead of a third colour being introduced.
 *
 * How, and why not the obvious way: a `linear-gradient` is a background *image*, and
 * background images do not interpolate, so the `135deg` in one cannot be animated. The usual
 * way round that is to register the angle with `@property` and animate it, but a custom
 * property feeding a gradient is a paint input — that repaints the gradient on the main
 * thread every frame, for as long as the screen is up, forever. So the gradient is left alone
 * and the layer carrying it is rotated instead: that is a transform, which the compositor
 * owns, and it costs the same whether it is turning or not.
 *
 * The cost of doing it that way is geometric. A layer that rotates inside the rim has to be
 * bigger than the rim, or its own corners swing into view part-way through the turn — and
 * once it is bigger, the gradient is stretched over a box larger than what is visible. The
 * two constants below are what that requires; both are derived rather than chosen, and
 * designTokens.test.ts holds them to their derivations.
 *
 * Kept out of desktopFallbackScale.ts deliberately: that file is the canvas's own pixels and
 * says so. None of this is in the canvas.
 */

/**
 * One full turn of the ramp, in seconds.
 *
 * The one number here that is to taste, and it is a judgement call between two failures: too
 * slow and the sweep is not read as motion at all, too fast and a ring travelling around a QR
 * code starts reading as a progress spinner — as though the code were still loading and not
 * yet ready to scan. Four is deliberately on the noticeable side of that.
 */
export const QR_RIM_SWEEP_SECONDS = 4;

/**
 * How much wider than the rim the turning layer is drawn.
 *
 * A square covers a square at every angle only if its side reaches the other's diagonal,
 * which is √2 ≈ 1.4142 times the side. This is a hair over, so nothing is left uncovered at
 * the diagonals where the margin is thinnest. (The rim is square — the card inside it is,
 * and the rim's padding is equal on all four sides — so one factor sizes both axes.)
 */
export const QR_RIM_SWEEP_OVERSIZE = 1.42;

/**
 * How far the ramp's endpoints are pulled in from the turning layer's edges.
 *
 * Oversizing the layer stretches the gradient across a box larger than the ring that shows,
 * which would put pure amber and pure purple outside the visible area and leave the rim
 * wearing only the washed-out middle of the ramp — the corner-to-corner contrast the canvas
 * asks for, quietly lost to a rendering detail.
 *
 * Pulling the stops in by the same proportion the layer was grown puts full-strength colour
 * back on the rim's own corners: `1 / OVERSIZE` of the gradient line is what shows, centred,
 * so half the remainder is dead space at each end. At rest the ring is then
 * indistinguishable from the still gradient, and the turn is the only thing added.
 */
export const QR_RIM_SWEEP_STOP_INSET = (1 - 1 / QR_RIM_SWEEP_OVERSIZE) / 2;

/**
 * The ramp the turning layer carries.
 *
 * Still the canvas's 135°, which is what puts the top and left edges on the same tone; any
 * steeper angle lands both side edges mid-ramp and muddies them. Only the stop positions
 * differ from the still version, and only to cancel the oversizing.
 */
export const buildSweepingRimRamp = (startColor: string, endColor: string): string => {
  const inset = QR_RIM_SWEEP_STOP_INSET * 100;

  return `linear-gradient(135deg, ${startColor} ${inset}%, ${endColor} ${100 - inset}%)`;
};
